/**
 * Centro de Soporte — Sprint P0.2 v1.46 (Feb 2026)
 *
 * Rediseño completo tipo mensajería moderna (WhatsApp/iMessage):
 *   • Lista de conversaciones tipo contactos con avatar, último mensaje
 *     y timestamp relativo ("hace 5 min", "ayer", "12 mar").
 *   • Panel de chat con separadores por día, agrupación de mensajes del
 *     mismo autor en la misma ventana (avatar solo en el primero),
 *     burbujas con cola, sombras finas, tiempos por burbuja al hover.
 *   • Composer pill con auto-resize + adjuntar + Enter=enviar +
 *     glassmorphism.
 *   • Header con presencia "En línea" (indicador verde pulsante) para
 *     el equipo de soporte cuando el thread está abierto.
 *   • Empty-state cinematográfico (icono grande + copy + CTA).
 *   • Botón "Volver a la lista" en móvil (mismo comportamiento que
 *     WhatsApp Web bajo 768px).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Send, Paperclip, Plus, X, LifeBuoy,
  CheckCircle2, User as UserIcon, Shield, MessageCircle, ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../contexts/LocaleContext';

type Attachment = { name: string; data_url: string; size?: number };
type Reply = {
  id: string;
  message: string;
  author_email?: string;
  author_role?: string;
  by_admin?: boolean;
  attachments?: Attachment[];
  created_at?: string;
  at?: string;
};
type Ticket = {
  id: string;
  user_id?: string;
  user_email?: string;
  user_name?: string;
  user_avatar_url?: string;
  subject: string;
  message: string;
  status: string;
  category?: string;
  workspace_id?: string | null;
  replies?: Reply[];
  created_at?: string;
  updated_at?: string;
};

// ── Utilities ──────────────────────────────────────────────────────
const dayKey = (iso?: string) => {
  if (!iso) return '';
  try { return new Date(iso).toDateString(); } catch { return ''; }
};

const relativeTime = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `hace ${mins}m`;
    const hours = Math.round(mins / 60);
    if (hours < 24 && d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'ayer';
    if (hours < 24 * 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

const daySeparatorLabel = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Hoy';
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

const hourMinute = (iso?: string) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

const initialsFromEmail = (email?: string) => {
  if (!email) return '?';
  // Sprint P0.3 v1.47 · If we received a full display name (contains a
  // space and no `@`), extract initials from the words.
  if (!email.includes('@') && /\s/.test(email)) {
    const parts = email.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
  }
  const clean = email.split('@')[0];
  const parts = clean.split(/[._\-+]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (clean.slice(0, 2) || '?').toUpperCase();
};

// Deterministic soft colour from a string (email → hue).
const avatarHue = (seed?: string) => {
  if (!seed) return 210;
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xff;
  return (h * 137) % 360;
};

const previewLine = (th: Ticket) => {
  const last = (th.replies && th.replies.length > 0) ? th.replies[th.replies.length - 1] : null;
  const text = last?.message || th.message || '';
  const prefix = last?.by_admin ? 'Soporte: ' : '';
  return (prefix + text).replace(/\s+/g, ' ').slice(0, 60);
};

export default function Soporte() {
  const nav = useNavigate();
  const t = useT();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [threads, setThreads] = useState<Ticket[]>([]);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [showNew, setShowNew] = useState(false);

  // Sprint P0.2 v1.46 · avoid reload() closing over `selected` — that
  // would recompute the callback on every thread click and cause the
  // list-poll interval to tear down + a false-positive spinner right
  // after creating a new thread. Instead, we keep a ref to `selected`
  // and sync it opportunistically after each list refresh.
  const selectedRef = useRef<Ticket | null>(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const reload = useCallback(async () => {
    // Only show the initial spinner. Subsequent refreshes update the
    // list in-place without flashing a spinner over already-visible
    // conversations.
    try {
      const raw = isAdmin
        ? await apiService.adminListTickets()
        : await apiService.listMyTickets();
      const list: Ticket[] = Array.isArray(raw) ? raw : (raw?.tickets || []);
      list.sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      });
      setThreads(list);
      const sel = selectedRef.current;
      if (sel) {
        const fresh = list.find((th) => th.id === sel.id);
        if (fresh) setSelected(fresh);
      }
    } catch {
      // Non-fatal — just show empty state.
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { reload(); }, [reload]);

  // Sprint P0.5 v1.48 · CONVERSACIÓN ÚNICA · auto-abrir el thread
  // activo del usuario al montar (solo lado usuario). El admin sigue
  // eligiendo qué hilo abrir manualmente.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (isAdmin) return;
    if (autoOpenedRef.current) return;
    if (selected) return;
    if (!threads.length) return;
    const active = threads.find((t) => t.status !== 'closed');
    if (active) {
      autoOpenedRef.current = true;
      // Open the whole thread (fetches replies too).
      void (async () => {
        try {
          const fresh = await apiService.getTicket(active.id);
          setSelected(fresh);
        } catch {
          setSelected(active);
        }
      })();
    }
  }, [threads, isAdmin, selected]);

  // Poll every 6s (was 8s) — faster than a modern chat feel.
  useEffect(() => {
    const int = setInterval(reload, 6000);
    return () => clearInterval(int);
  }, [reload]);

  const openThread = async (th: Ticket) => {
    setSelected(th);
    try {
      const fresh = await apiService.getTicket(th.id);
      setSelected(fresh);
    } catch { /* keep summary */ }
  };

  const startThread = async () => {
    if (!newSubject.trim() || !newMessage.trim()) {
      toast.error(t('support.errorFieldsRequired'));
      return;
    }
    setStarting(true);
    try {
      const r = await apiService.createTicket(newSubject.trim(), newMessage.trim());
      toast.success(t('support.threadCreated'));
      setShowNew(false);
      setNewSubject(''); setNewMessage('');
      await reload();
      if (r?.ticket) setSelected(r.ticket);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('support.errorCreate'));
    } finally {
      setStarting(false);
    }
  };

  // Mobile "back to list" toggle.
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  useEffect(() => { if (selected) setMobilePanelOpen(true); }, [selected]);

  return (
    <div
      className="h-[100dvh] flex flex-col nx-circuit"
      style={{ background: 'hsl(var(--bg))', color: 'hsl(var(--text))' }}
      data-testid="soporte-page"
    >
      <header
        className="sticky top-0 z-20 backdrop-blur-xl shrink-0"
        style={{
          background: 'linear-gradient(180deg, hsl(var(--bg-elev) / 0.92), hsl(var(--bg-elev) / 0.55))',
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => nav('/home')}
            className="inline-flex items-center gap-2 text-sm hover:opacity-90 shrink-0"
            style={{ color: 'hsl(var(--text-muted))' }}
            data-testid="soporte-back"
          >
            <ArrowLeft size={16} /> <span className="hidden sm:inline">{t('common.back')}</span>
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-8 h-8 rounded-full grid place-items-center shrink-0"
              style={{ background: 'hsl(var(--accent) / 0.14)', color: 'hsl(var(--accent))', border: '1px solid hsl(var(--accent) / 0.35)' }}
            >
              <MessageCircle size={15} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: 'hsl(var(--text))' }}>
                {isAdmin ? t('support.titleAdmin') : t('support.title')}
              </div>
              <div className="text-[10px] tracking-widest uppercase" style={{ color: 'hsl(var(--text-muted))' }}>
                {isAdmin ? 'Panel de soporte' : 'Chat con el equipo Nexus'}
              </div>
            </div>
          </div>
          <div className="w-16 sm:w-24" />
        </div>
      </header>

      <main className="flex-1 min-h-0">
        <div className="h-full max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-[340px_minmax(0,1fr)]">
          {/* ── Threads list ─────────────────────────────────────── */}
          <aside
            className={`min-w-0 h-full flex flex-col ${mobilePanelOpen && selected ? 'hidden md:flex' : 'flex'}`}
            style={{ borderRight: '1px solid hsl(var(--border))', background: 'hsl(var(--bg-elev) / 0.35)' }}
            data-testid="soporte-threads-list"
          >
            {!isAdmin && (() => {
              // Sprint P0.5 v1.48 · Conversación única por usuario.
              // Si ya hay un thread NO cerrado, no ofrecemos "Nueva
              // conversación" — el usuario continúa en ese chat.
              const hasActive = threads.some((t) => t.status !== 'closed');
              if (hasActive) {
                return (
                  <div className="p-3 shrink-0">
                    <div
                      className="rounded-xl px-3 py-2.5 text-[11px] leading-tight"
                      style={{
                        background: 'hsl(var(--accent) / 0.10)',
                        border: '1px solid hsl(var(--accent) / 0.28)',
                        color: 'hsl(var(--text-muted))',
                      }}
                      data-testid="soporte-single-thread-hint"
                    >
                      <span style={{ color: 'hsl(var(--accent))', fontWeight: 600 }}>Chat continuo</span>
                      <span> · escribe abajo para seguir la conversación con el equipo.</span>
                    </div>
                  </div>
                );
              }
              return (
                <div className="p-3 shrink-0">
                  <button
                    onClick={() => setShowNew(true)}
                    data-testid="soporte-new-thread"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-transform active:scale-[0.98]"
                    style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
                  >
                    <Plus size={14} /> {t('support.newThread')}
                  </button>
                </div>
              );
            })()}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {loading ? (
                <div className="p-8 flex items-center justify-center" data-testid="soporte-loading">
                  <Loader2 size={18} className="animate-spin" style={{ color: 'hsl(var(--text-muted))' }} />
                </div>
              ) : threads.length === 0 ? (
                <div className="p-8 text-center space-y-4" data-testid="soporte-empty">
                  <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full"
                    style={{ background: 'hsl(var(--accent) / 0.12)', color: 'hsl(var(--accent))', border: '1px solid hsl(var(--accent) / 0.25)' }}
                  >
                    <LifeBuoy size={26} />
                  </div>
                  <div className="text-sm" style={{ color: 'hsl(var(--text))' }}>
                    {isAdmin ? t('support.emptyAdmin') : t('support.emptyUser')}
                  </div>
                  {!isAdmin && (
                    <button
                      onClick={() => setShowNew(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold"
                      style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
                      data-testid="soporte-empty-start"
                    >
                      <Plus size={13} /> Empezar una conversación
                    </button>
                  )}
                </div>
              ) : (
                <ul>
                  {threads.map((th) => {
                    const active = selected?.id === th.id;
                    const seed = isAdmin ? (th.user_email || th.user_id) : 'soporte-nexus';
                    const hue = avatarHue(seed);
                    const primaryLabel = isAdmin
                      ? (th.user_name || (th.user_email ? th.user_email.split('@')[0] : 'Cliente'))
                      : (t('support.teamName') || 'Nexus Soporte');
                    const secondaryLabel = isAdmin ? (th.user_email || '') : '';
                    return (
                      <li key={th.id}>
                        <button
                          onClick={() => openThread(th)}
                          data-testid={`soporte-thread-${th.id}`}
                          className="w-full text-left px-4 py-3 transition-colors flex items-center gap-3"
                          style={{
                            background: active ? 'hsl(var(--accent) / 0.10)' : 'transparent',
                            borderLeft: active ? '3px solid hsl(var(--accent))' : '3px solid transparent',
                          }}
                        >
                          {isAdmin && th.user_avatar_url ? (
                            <img
                              src={th.user_avatar_url}
                              alt={th.user_name || th.user_email || 'Cliente'}
                              className="w-11 h-11 rounded-full shrink-0 object-cover"
                              style={{
                                border: `1px solid hsl(${hue} 65% 45% / 0.5)`,
                                boxShadow: `0 4px 14px hsl(${hue} 65% 25% / 0.25)`,
                              }}
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div
                              className="w-11 h-11 rounded-full grid place-items-center shrink-0 text-sm font-semibold"
                              style={{
                                background: `hsl(${hue} 65% 45% / 0.22)`,
                                color: `hsl(${hue} 90% 78%)`,
                                border: `1px solid hsl(${hue} 65% 45% / 0.45)`,
                                boxShadow: `0 4px 14px hsl(${hue} 65% 25% / 0.25)`,
                              }}
                            >
                              {isAdmin ? initialsFromEmail(th.user_email || th.user_name) : 'N'}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold truncate flex-1" style={{ color: 'hsl(var(--text))' }}>
                                {primaryLabel}
                              </span>
                              <span className="text-[10px] tabular-nums shrink-0" style={{ color: 'hsl(var(--text-muted))' }}>
                                {relativeTime(th.updated_at || th.created_at)}
                              </span>
                            </div>
                            {isAdmin && secondaryLabel && (
                              <div className="text-[11px] truncate mt-px" style={{ color: 'hsl(var(--text-muted))' }}>
                                {secondaryLabel}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-0.5">
                              <StatusDot status={th.status} />
                              <span className="text-[12px] truncate flex-1" style={{ color: 'hsl(var(--text-muted))' }}>
                                {previewLine(th) || th.subject}
                              </span>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          {/* ── Chat panel ───────────────────────────────────────── */}
          <section
            className={`min-w-0 h-full flex flex-col ${!mobilePanelOpen && !selected ? 'hidden md:flex' : 'flex'}`}
            data-testid="soporte-chat-panel"
          >
            {!selected ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-sm">
                  <div
                    className="mx-auto w-20 h-20 rounded-full grid place-items-center mb-5"
                    style={{
                      background: 'radial-gradient(circle at 30% 30%, hsl(var(--accent) / 0.2), hsl(var(--accent) / 0.05))',
                      border: '1px solid hsl(var(--accent) / 0.28)',
                      color: 'hsl(var(--accent))',
                    }}
                  >
                    <MessageCircle size={32} />
                  </div>
                  <div className="text-lg font-semibold mb-2" style={{ color: 'hsl(var(--text))' }}>
                    Habla con el equipo Nexus
                  </div>
                  <div className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
                    {t('support.pickThreadHint') || 'Selecciona una conversación o empieza una nueva. Respondemos en minutos.'}
                  </div>
                </div>
              </div>
            ) : (
              <ThreadChat
                ticket={selected}
                isAdmin={isAdmin}
                onRefresh={reload}
                onBack={() => { setMobilePanelOpen(false); setSelected(null); }}
              />
            )}
          </section>
        </div>
      </main>

      {/* ── New thread modal ─────────────────────────────────────── */}
      {showNew && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-md"
          onClick={() => setShowNew(false)}
          data-testid="soporte-new-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-[520px] max-w-[95vw] rounded-2xl p-6 shadow-2xl"
            style={{
              background: 'hsl(var(--bg-elev))',
              border: '1px solid hsl(var(--border))',
              boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
            }}
          >
            <div className="text-lg font-semibold mb-1" style={{ color: 'hsl(var(--text))' }}>
              {t('support.newThread')}
            </div>
            <div className="text-xs mb-5" style={{ color: 'hsl(var(--text-muted))' }}>
              {t('support.newHint')}
            </div>
            <input
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder={t('support.subjectPlaceholder')}
              className="w-full mb-3 px-3.5 py-2.5 rounded-xl outline-none text-sm"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
              data-testid="soporte-new-subject"
            />
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={t('support.messagePlaceholder')}
              rows={5}
              className="w-full px-3.5 py-2.5 rounded-xl outline-none text-sm resize-none"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }}
              data-testid="soporte-new-message"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowNew(false)}
                className="px-4 py-2 rounded-full text-sm"
                style={{ color: 'hsl(var(--text-muted))', background: 'transparent' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={startThread}
                disabled={starting || !newSubject.trim() || !newMessage.trim()}
                className="px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-40 shadow-sm"
                style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
                data-testid="soporte-new-submit"
              >
                {starting ? <Loader2 size={13} className="animate-spin" /> : t('support.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Chat thread panel ───────────────────────────────────────────────

const ThreadChat: React.FC<{
  ticket: Ticket;
  isAdmin: boolean;
  onRefresh: () => void;
  onBack: () => void;
}> = ({ ticket, isAdmin, onRefresh, onBack }) => {
  const t = useT();
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<Attachment[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [ticket.id, ticket.replies?.length]);

  // Auto-resize textarea (grows up to 5 lines).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [reply]);

  // Flatten conversation into an ordered list.
  const items = useMemo(() => {
    const base: Reply[] = [{
      id: 'root',
      message: ticket.message,
      author_email: ticket.user_email,
      author_role: 'user',
      by_admin: false,
      attachments: [],
      created_at: ticket.created_at,
    }];
    return base.concat(ticket.replies || []);
  }, [ticket]);

  // Group by day + collapse consecutive same-author messages within 5min.
  type Block = { type: 'day'; label: string; key: string }
    | { type: 'msg'; reply: Reply; mine: boolean; showAvatar: boolean; showTail: boolean; key: string };
  const blocks: Block[] = useMemo(() => {
    const out: Block[] = [];
    let lastDay = '';
    let lastAuthor = '';
    let lastTs = 0;
    items.forEach((it, idx) => {
      const iso = it.created_at || it.at;
      const d = dayKey(iso);
      if (d && d !== lastDay) {
        out.push({ type: 'day', label: daySeparatorLabel(iso), key: `day-${d}` });
        lastDay = d;
        lastAuthor = '';
      }
      const mine = isAdmin ? !!it.by_admin : !it.by_admin;
      const author = it.by_admin ? 'admin' : (it.author_email || 'user');
      const ts = iso ? new Date(iso).getTime() : 0;
      const withinFive = ts && lastTs && ts - lastTs < 5 * 60 * 1000;
      const showAvatar = author !== lastAuthor || !withinFive;
      // Show a "tail" only on the last message of a group (i.e. the
      // next item is different author OR is a day separator).
      const next = items[idx + 1];
      const nextAuthor = next ? (next.by_admin ? 'admin' : (next.author_email || 'user')) : '__end__';
      const nextTs = next && (next.created_at || next.at) ? new Date((next.created_at || next.at) as string).getTime() : 0;
      const nextSameGroup = author === nextAuthor && nextTs && ts && nextTs - ts < 5 * 60 * 1000;
      out.push({
        type: 'msg', reply: it, mine, showAvatar,
        showTail: !nextSameGroup,
        key: it.id + '-' + idx,
      });
      lastAuthor = author;
      lastTs = ts;
    });
    return out;
  }, [items, isAdmin]);

  const pickFiles = () => fileRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const attachments: Attachment[] = [];
    for (const f of files) {
      if (f.size > 4 * 1024 * 1024) {
        toast.error(t('support.attachTooLarge', { name: f.name }));
        continue;
      }
      const b64 = await new Promise<string>((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ''));
        r.readAsDataURL(f);
      });
      attachments.push({ name: f.name, data_url: b64, size: f.size });
    }
    setPending((p) => [...p, ...attachments]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const send = async (closeAfter = false) => {
    const msg = reply.trim();
    if (!msg && pending.length === 0) return;
    setSending(true);
    try {
      if (isAdmin) {
        await apiService.adminReplyTicket(ticket.id, msg, closeAfter, pending);
      } else {
        await apiService.replyTicket(ticket.id, msg, pending);
      }
      setReply(''); setPending([]);
      await onRefresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('support.errorSend'));
    } finally {
      setSending(false);
    }
  };

  const otherSeed = isAdmin ? (ticket.user_email || ticket.user_id) : 'soporte-nexus';
  const otherHue = avatarHue(otherSeed);
  const otherPrimary = isAdmin
    ? (ticket.user_name || (ticket.user_email ? ticket.user_email.split('@')[0] : 'Cliente'))
    : (t('support.teamName') || 'Nexus Soporte');
  const otherSecondary = isAdmin ? (ticket.user_email || '') : '';

  return (
    <div className="h-full flex flex-col" data-testid="soporte-thread-panel">
      {/* Chat header — presence + status */}
      <header
        className="px-4 py-3 flex items-center gap-3 shrink-0 backdrop-blur-md"
        style={{
          borderBottom: '1px solid hsl(var(--border))',
          background: 'linear-gradient(180deg, hsl(var(--bg-elev) / 0.85), hsl(var(--bg-elev) / 0.55))',
        }}
      >
        <button
          onClick={onBack}
          className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0"
          style={{ background: 'hsl(var(--bg))', color: 'hsl(var(--text-muted))' }}
          aria-label="Volver a la lista"
          data-testid="soporte-mobile-back"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="relative shrink-0">
          {isAdmin && ticket.user_avatar_url ? (
            <img
              src={ticket.user_avatar_url}
              alt={otherPrimary}
              className="w-11 h-11 rounded-full object-cover"
              style={{ border: `1px solid hsl(${otherHue} 65% 45% / 0.5)` }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div
              className="w-11 h-11 rounded-full grid place-items-center text-sm font-semibold"
              style={{
                background: `hsl(${otherHue} 65% 45% / 0.22)`,
                color: `hsl(${otherHue} 90% 78%)`,
                border: `1px solid hsl(${otherHue} 65% 45% / 0.45)`,
              }}
            >
              {isAdmin ? initialsFromEmail(ticket.user_email || ticket.user_name) : 'N'}
            </div>
          )}
          {ticket.status !== 'closed' && (
            <span
              className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full"
              style={{
                background: 'hsl(155 65% 55%)',
                border: '2px solid hsl(var(--bg))',
                boxShadow: '0 0 8px hsl(155 65% 55% / 0.6)',
              }}
              aria-label="En línea"
              data-testid="soporte-presence-dot"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate" style={{ color: 'hsl(var(--text))' }}>
            {otherPrimary}
          </div>
          {isAdmin && otherSecondary && (
            <div className="text-[11px] truncate" style={{ color: 'hsl(var(--text-muted))' }} data-testid="soporte-user-email-header">
              {otherSecondary}
            </div>
          )}
          <div className="text-[11px] flex items-center gap-1.5 mt-px" style={{ color: 'hsl(var(--text-muted))' }}>
            <StatusPill status={ticket.status} />
            <span className="truncate">· {ticket.subject}</span>
          </div>
        </div>
      </header>

      {/* Scrollable message area */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-6 py-5"
        style={{
          background: 'radial-gradient(80vw 60vh at 50% 0%, hsl(var(--accent) / 0.03), transparent 60%), hsl(var(--bg))',
        }}
        data-testid="soporte-messages"
      >
        <div className="max-w-3xl mx-auto space-y-1.5">
          {blocks.map((b) => b.type === 'day' ? (
            <div
              key={b.key}
              className="flex items-center justify-center py-3"
            >
              <span
                className="text-[10px] tracking-widest uppercase font-medium px-3 py-1 rounded-full backdrop-blur-sm"
                style={{
                  color: 'hsl(var(--text-muted))',
                  background: 'hsl(var(--bg-elev) / 0.65)',
                  border: '1px solid hsl(var(--border))',
                }}
                data-testid="soporte-day-separator"
              >
                {b.label}
              </span>
            </div>
          ) : (
            <MessageBubble
              key={b.key}
              reply={b.reply}
              mine={b.mine}
              showAvatar={b.showAvatar}
              showTail={b.showTail}
            />
          ))}
        </div>
      </div>

      {/* Composer or closed-state banner */}
      {ticket.status === 'closed' ? (
        <div
          className="px-4 py-3 text-center text-xs shrink-0"
          style={{
            borderTop: '1px solid hsl(var(--border))',
            background: 'hsl(var(--bg-elev) / 0.5)',
            color: 'hsl(var(--text-muted))',
          }}
          data-testid="soporte-closed-banner"
        >
          Esta conversación está cerrada. Abre una nueva si necesitas ayuda.
        </div>
      ) : (
        <div
          className="px-3 sm:px-6 py-3 shrink-0"
          style={{
            borderTop: '1px solid hsl(var(--border))',
            background: 'linear-gradient(0deg, hsl(var(--bg-elev) / 0.95), hsl(var(--bg-elev) / 0.6))',
            backdropFilter: 'blur(16px)',
          }}
        >
          {pending.length > 0 && (
            <div className="max-w-3xl mx-auto flex flex-wrap gap-2 mb-2" data-testid="soporte-pending-attachments">
              {pending.map((a, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px]"
                  style={{
                    background: 'hsl(var(--accent) / 0.12)',
                    border: '1px solid hsl(var(--accent) / 0.3)',
                    color: 'hsl(var(--accent))',
                  }}
                >
                  <Paperclip size={10} /> {a.name}
                  <button onClick={() => setPending((p) => p.filter((_, k) => k !== i))} className="ml-1 opacity-70 hover:opacity-100" aria-label="Quitar adjunto">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <input ref={fileRef} type="file" multiple hidden onChange={onFile} accept="image/*,application/pdf,text/*" data-testid="soporte-file-input" />
            <button
              onClick={pickFiles}
              className="w-11 h-11 rounded-full grid place-items-center shrink-0 transition-colors"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
              aria-label={t('support.attach')}
              data-testid="soporte-attach-btn"
            >
              <Paperclip size={14} />
            </button>
            <div
              className="flex-1 flex items-end rounded-3xl px-4 py-2"
              style={{
                background: 'hsl(var(--bg))',
                border: '1px solid hsl(var(--border))',
                minHeight: 44,
              }}
            >
              <textarea
                ref={textareaRef}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(false); } }}
                rows={1}
                placeholder={t('support.replyPlaceholder')}
                className="flex-1 bg-transparent outline-none text-sm resize-none py-1.5"
                style={{ color: 'hsl(var(--text))', maxHeight: 140 }}
                data-testid="soporte-reply-input"
              />
            </div>
            <button
              onClick={() => send(false)}
              disabled={sending || (!reply.trim() && pending.length === 0)}
              className="w-11 h-11 rounded-full grid place-items-center shrink-0 disabled:opacity-40 transition-transform active:scale-95"
              style={{
                background: 'hsl(var(--accent))',
                color: 'hsl(210 40% 8%)',
                boxShadow: '0 6px 16px hsl(var(--accent) / 0.35)',
              }}
              aria-label={t('support.send')}
              data-testid="soporte-send-btn"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
            {isAdmin && (
              <button
                onClick={() => send(true)}
                disabled={sending}
                className="hidden lg:inline-flex items-center gap-1 px-3 h-11 rounded-full text-xs font-semibold"
                style={{ background: 'hsl(150 45% 45% / 0.15)', border: '1px solid hsl(150 45% 45% / 0.45)', color: 'hsl(150 65% 60%)' }}
                data-testid="soporte-close-btn"
                title={t('support.closeThread')}
              >
                <CheckCircle2 size={12} /> {t('support.closeThread')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Message bubble ─────────────────────────────────────────────────

const MessageBubble: React.FC<{
  reply: Reply;
  mine: boolean;
  showAvatar: boolean;
  showTail: boolean;
}> = ({ reply, mine, showAvatar, showTail }) => {
  const when = reply.created_at || reply.at;
  const seed = reply.by_admin ? 'soporte-nexus' : (reply.author_email || 'user');
  const hue = avatarHue(seed);
  const initials = reply.by_admin ? 'N' : initialsFromEmail(reply.author_email || '');
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'} ${showAvatar ? 'mt-3' : 'mt-0.5'}`}>
      <div className={`flex items-end gap-2 max-w-[85%] sm:max-w-[70%] ${mine ? 'flex-row-reverse' : ''}`}>
        {/* Avatar slot — invisible placeholder for grouped messages keeps alignment */}
        <div className="w-8 h-8 shrink-0" aria-hidden={!showAvatar}>
          {showAvatar && (
            <div
              className="w-8 h-8 rounded-full grid place-items-center text-[10px] font-bold"
              style={reply.by_admin
                ? { background: 'hsl(var(--accent) / 0.18)', color: 'hsl(var(--accent))', border: '1px solid hsl(var(--accent) / 0.4)' }
                : { background: `hsl(${hue} 65% 45% / 0.22)`, color: `hsl(${hue} 90% 78%)`, border: `1px solid hsl(${hue} 65% 45% / 0.45)` }
              }
              title={reply.by_admin ? 'Nexus Soporte' : (reply.author_email || 'Cliente')}
            >
              {reply.by_admin ? <Shield size={12} /> : (initials || <UserIcon size={12} />)}
            </div>
          )}
        </div>
        <div className={`min-w-0 flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
          <div
            className="text-sm leading-relaxed whitespace-pre-wrap px-3.5 py-2.5 relative"
            style={{
              background: mine ? 'hsl(var(--accent) / 0.18)' : 'hsl(var(--bg-elev) / 0.85)',
              border: `1px solid ${mine ? 'hsl(var(--accent) / 0.35)' : 'hsl(var(--border))'}`,
              color: 'hsl(var(--text))',
              // Bubble tail shape: sharp corner on the side of the tail.
              borderTopLeftRadius: !mine && showAvatar ? 6 : 18,
              borderTopRightRadius: mine && showAvatar ? 6 : 18,
              borderBottomLeftRadius: !mine && showTail ? 6 : 18,
              borderBottomRightRadius: mine && showTail ? 6 : 18,
              boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
            }}
            data-testid={`soporte-msg-${reply.id}`}
          >
            {reply.message}
            {reply.attachments && reply.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {reply.attachments.map((a, i) => {
                  const isImage = (a.data_url || '').startsWith('data:image/');
                  if (isImage) {
                    return (
                      <a key={i} href={a.data_url} target="_blank" rel="noreferrer"
                         className="block rounded-lg overflow-hidden"
                         style={{ border: '1px solid hsl(var(--border))', maxWidth: 240 }}>
                        <img src={a.data_url} alt={a.name} className="block w-full h-auto" />
                      </a>
                    );
                  }
                  return (
                    <a key={i} href={a.data_url} download={a.name}
                       className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]"
                       style={{
                         background: 'hsl(var(--bg))',
                         border: '1px solid hsl(var(--border))',
                         color: 'hsl(var(--text-muted))',
                       }}>
                      <Paperclip size={10} /> {a.name}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
          {showTail && (
            <div
              className={`mt-1 text-[10px] tabular-nums px-1 ${mine ? 'text-right' : 'text-left'}`}
              style={{ color: 'hsl(var(--text-muted))' }}
              data-testid={`soporte-msg-time-${reply.id}`}
            >
              {hourMinute(when)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  const color = status === 'closed' ? 'hsl(var(--text-muted))' :
                status === 'answered' ? 'hsl(155 65% 60%)' :
                status === 'in_progress' ? 'hsl(38 92% 60%)' :
                'hsl(210 100% 65%)';
  return (
    <span
      className="w-2 h-2 rounded-full shrink-0"
      style={{
        background: color,
        boxShadow: status !== 'closed' ? `0 0 8px ${color}` : 'none',
      }}
    />
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const t = useT();
  const map: Record<string, { key: string; color: string }> = {
    open: { key: 'support.statusOpen', color: 'hsl(210 100% 65%)' },
    answered: { key: 'support.statusAnswered', color: 'hsl(155 65% 60%)' },
    in_progress: { key: 'support.statusInProgress', color: 'hsl(38 92% 60%)' },
    closed: { key: 'support.statusClosed', color: 'hsl(var(--text-muted))' },
  };
  const m = map[status] || { key: 'support.statusUnknown', color: 'hsl(var(--text-muted))' };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-medium"
      style={{
        color: m.color,
        background: m.color.replace(')', ' / 0.14)'),
        border: `1px solid ${m.color.replace(')', ' / 0.35)')}`,
      }}
    >
      {t(m.key)}
    </span>
  );
};
